import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { custoDaObra, type CustoDaMaoDeObra } from './custo-da-mao-de-obra';

/**
 * O custo da mão de obra da obra aberta, e o provisório que se grava nele (Fase 29, etapa 3 —
 * RN-399).
 *
 * É o que a aba de itens passa às duas tabelas — a de itens por serviço, que só lê, e a
 * consolidação, que decide —, para que as duas mostrem **o mesmo** R$/h de cada função e a
 * gravação de um provisório apareça nas duas sem rebusca de cada uma.
 */
export interface CustoDaMaoDeObraNaTela {
  /** `undefined` = ainda lendo, ou a leitura falhou (`erro` diz). */
  readonly custo: CustoDaMaoDeObra | undefined;
  /** `takeoff.manage` (D13): quem só vê o quantitativo vê o R$/h, e não grava provisório. */
  readonly podeEditar: boolean;
  /** A leitura que não veio — dita, para "sem custo" não parecer a resposta. */
  readonly erro?: string;
  /**
   * Grava — `null` tira — o R$/h provisório de uma chave da consolidação (`funcao:<id>` ou
   * `descricao:<…>`) e relê o custo da obra. `false` = recusado; o aviso já foi dado.
   */
  readonly definirProvisorio: (key: string, perHour: number | null) => Promise<boolean>;
}

interface Lido {
  readonly de: ProjectId;
  readonly custo: CustoDaMaoDeObra;
}

/**
 * Lê o custo uma vez por obra e o relê depois de cada provisório gravado.
 *
 * ## Fora do `useCommand`, como os preços de material
 *
 * O `useCommand` avisa o mundo a cada sucesso, e toda leitura viva da página rebusca — a
 * estrutura, o quantitativo, o desenho. Por um R$/h, isso remontaria a bancada no meio de um
 * lançamento. A gravação vai na hora, fora do rascunho da bancada (o provisório é da obra, e
 * descartar o rascunho não pode levá-lo), e a recusa vira o mesmo aviso que o `useCommand`
 * daria.
 *
 * ## Relida, e não remendada
 *
 * O servidor devolve só o provisório gravado. O R$/h que passa a valer depende da folha —
 * com gente na folha, o provisório não muda nada (D11) —, e a tela não vê a folha (D14).
 * Então a tela relê, em vez de adivinhar.
 *
 * ## A leitura que falha não derruba a aba
 *
 * Sem o custo, a mão de obra fica sem R$/h, dito na tabela; o quantitativo continua de pé.
 * Por isso a chamada vai dentro de uma promessa: até o erro que ela lançaria na hora vira a
 * recusa dita, e não uma tela quebrada.
 *
 * A resposta que chega depois de trocar de obra é descartada: as chaves das duas obras são
 * as mesmas (`funcao:<id>`), e o custo de uma não pode cair na outra.
 */
export function useCustoDaMaoDeObra(): CustoDaMaoDeObraNaTela {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const podeEditar = usePermission(PERMISSION.TAKEOFF_MANAGE, activeProjectId);

  const [lido, setLido] = useState<Lido | undefined>(undefined);
  const [erro, setErro] = useState<string | undefined>(undefined);

  /** A obra aberta agora — o que as respostas atrasadas conferem antes de cair na tela. */
  const obraAtual = useRef(activeProjectId);
  obraAtual.current = activeProjectId;

  /** Lê o custo da obra; `string` = a recusa, já em português. */
  const ler = useCallback(
    async (projectId: ProjectId): Promise<Omit<Lido, 'de'> | string> => {
      if (session === undefined) return 'Sem sessão.';
      try {
        const resultado = await Promise.resolve().then(() =>
          container.getProjectLaborRates.execute({ projectId }, session),
        );
        return resultado.ok
          ? { custo: custoDaObra(resultado.value) }
          : messageForCode(resultado.error.code, resultado.error.message);
      } catch {
        return 'O custo da mão de obra não foi lido — verifique a conexão.';
      }
    },
    [container, session],
  );

  useEffect(() => {
    if (session === undefined || activeProjectId === undefined) return undefined;
    let vivo = true;
    setErro(undefined);
    void ler(activeProjectId).then((resultado) => {
      if (!vivo) return;
      if (typeof resultado === 'string') setErro(resultado);
      else setLido({ de: activeProjectId, ...resultado });
    });
    return () => {
      vivo = false;
    };
    // A sessão muda de objeto sem mudar de pessoa; quem decide reler é a pessoa e a obra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, session?.userId, activeProjectId]);

  const lidoDaObra = lido !== undefined && lido.de === activeProjectId ? lido : undefined;
  const daObra = lidoDaObra?.custo;

  const definirProvisorio = useCallback(
    async (key: string, perHour: number | null): Promise<boolean> => {
      if (session === undefined || activeProjectId === undefined) return false;
      const projectId = activeProjectId;
      try {
        const resultado = await container.setLaborProvisionalRate.execute(
          { projectId, key, perHour },
          session,
        );
        if (!resultado.ok) {
          toast.error(messageForCode(resultado.error.code, resultado.error.message));
          return false;
        }
      } catch {
        toast.error('O provisório não foi gravado — verifique a conexão.');
        return false;
      }
      if (obraAtual.current !== projectId) return false;
      const relido = await ler(projectId);
      if (typeof relido === 'string') {
        // Gravou, mas a tela não sabe o R$/h novo: dizer, em vez de mostrar o antigo calado.
        toast.warning(`O provisório foi gravado, mas o custo não foi relido: ${relido}`);
        return true;
      }
      if (obraAtual.current === projectId) {
        setErro(undefined);
        setLido({ de: projectId, ...relido });
      }
      return true;
    },
    [container, session, activeProjectId, ler],
  );

  return {
    custo: daObra,
    podeEditar,
    ...(erro === undefined ? {} : { erro }),
    definirProvisorio,
  };
}
